import { useEffect, useRef, useState } from "react";
import { Save, Send, RotateCcw, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { errMsg } from "@/lib/api";
import { useApi, PageHead } from "@/admin/AdminApp";
import BulkEmail from "@/admin/BulkEmail";

const PRESETS = {
  gmail: ["Gmail", "smtp.gmail.com", 587, "starttls"], outlook: ["Outlook / Office 365", "smtp.office365.com", 587, "starttls"],
  zoho: ["Zoho", "smtp.zoho.com", 587, "starttls"], ses: ["Amazon SES", "email-smtp.us-east-1.amazonaws.com", 587, "starttls"], custom: ["Custom", "", 587, "starttls"],
};
const TABS = [["smtp", "SMTP"], ["templates", "Templates"], ["bulk", "Bulk email"], ["notifications", "Notifications"], ["log", "Send log"]];

const Smtp = ({ data, onSaved }) => {
  const api = useApi();
  const [f, setF] = useState({ ...data.smtp, password: "" });
  const [to, setTo] = useState("");
  const [busy, setBusy] = useState("");
  const preset = (k) => { const [, host, port, security] = PRESETS[k]; setF({ ...f, preset: k, host: host || f.host, port, security }); };
  const save = async () => {
    setBusy("save");
    try { onSaved(await api("put", "/email/smtp", f)); setF((x) => ({ ...x, password: "" })); toast.success("SMTP saved"); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); }
  };
  const test = async () => {
    setBusy("test");
    try { await api("post", "/email/test", { to }); toast.success("Test email sent"); } catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); }
  };
  const inp = (k, l, type = "text", ph = "") => (
    <label className="block"><span className="label-lux">{l}</span><input type={type} placeholder={ph} className="input-lux" value={f[k] ?? ""} onChange={(e) => setF({ ...f, [k]: type === "number" ? Number(e.target.value) : e.target.value })} data-testid={`smtp-${k}-input`} /></label>
  );
  return (
    <div className="space-y-6">
      <div className="flex flex-wrap gap-2">
        {Object.entries(PRESETS).map(([k, [l]]) => <button key={k} onClick={() => preset(k)} className={`rounded-full border px-4 py-1.5 text-sm ${f.preset === k ? "border-[var(--ink)] bg-[var(--ink)] text-[var(--ivory)]" : "border-[var(--line)] bg-white"}`} data-testid={`smtp-preset-${k}`}>{l}</button>)}
      </div>
      <div className="grid gap-4 rounded-2xl border border-[var(--line)] bg-white p-6 sm:grid-cols-2">
        {inp("host", "SMTP host")}{inp("port", "Port", "number")}
        <label className="block"><span className="label-lux">Security</span>
          <select className="input-lux" value={f.security} onChange={(e) => setF({ ...f, security: e.target.value })} data-testid="smtp-security-select"><option value="starttls">STARTTLS</option><option value="ssl">SSL</option></select></label>
        {inp("username", "Username")}
        {inp("password", data.smtp.has_password ? "Password (saved, encrypted — leave blank to keep)" : "Password / app password", "password", data.smtp.has_password ? "••••••••" : "")}
        {inp("from_email", "From email")}{inp("from_name", "From name")}
        <div className="flex items-end"><button className="btn-ink w-full" onClick={save} disabled={!!busy} data-testid="smtp-save-btn">{busy === "save" ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} Save SMTP</button></div>
      </div>
      {f.preset === "gmail" && <p className="text-sm text-slate-500">Gmail needs an App Password (Google Account → Security → App passwords), not your normal password.</p>}
      <div className="flex flex-col gap-2 rounded-2xl border border-[var(--line)] bg-white p-6 sm:flex-row">
        <input className="input-lux" placeholder="Send a test email to…" value={to} onChange={(e) => setTo(e.target.value)} data-testid="smtp-test-to-input" />
        <button className="btn-ghost shrink-0" onClick={test} disabled={!!busy || !to} data-testid="smtp-test-btn">{busy === "test" ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />} Send test</button>
      </div>
    </div>
  );
};

const FREQS = [[1, "Every day"], [2, "Every 2 days"], [3, "Every 3 days"], [5, "Every 5 days"], [7, "Every week"], [14, "Every 2 weeks"]];
const hourLabel = (h) => `${((h + 11) % 12) + 1}:00 ${h < 12 ? "AM" : "PM"}`;

const ReminderCard = ({ tkey }) => {
  const api = useApi();
  const [info, setInfo] = useState(null);
  const [r, setR] = useState(null);
  const [aud, setAud] = useState(null);
  useEffect(() => {
    setR(null);
    setAud(null);
    api("get", "/reminders").then((d) => { setInfo(d); setR(d.reminders[tkey] || null); }).catch(() => {});
    api("post", `/reminders/${tkey}/preview-audience`).then(setAud).catch(() => {});
  }, [api, tkey]);
  if (!info) return null;
  if (!r) return <p className="rounded-2xl border border-[var(--line)] bg-white p-5 text-sm text-slate-500" data-testid="reminder-not-available">This email is only sent when a guest asks for their code, so it can't be scheduled.</p>;
  const save = async (patch) => {
    const next = { ...r, ...patch };
    setR(next);
    try { await api("put", `/reminders/${tkey}`, next); toast.success(next.enabled ? "Reminder schedule saved" : "Reminders turned off"); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="rounded-2xl border border-[var(--line)] bg-white p-5" data-testid="reminder-card">
      <div className="flex items-center justify-between gap-3">
        <div><div className="text-[15px]">Automatic reminders</div><div className="text-sm text-slate-500">{info.audiences[tkey]}{aud ? ` · ${aud.count} right now` : ""}</div></div>
        <Switch checked={r.enabled} onCheckedChange={(v) => save({ enabled: v })} data-testid="reminder-enabled-switch" />
      </div>
      {r.enabled && (
        <div className="mt-4 grid gap-3 sm:grid-cols-2">
          <label className="block"><span className="label-lux">How often</span>
            <select className="input-lux" value={r.every_days} onChange={(e) => save({ every_days: Number(e.target.value) })} data-testid="reminder-frequency-select">
              {FREQS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select></label>
          <label className="block"><span className="label-lux">Send at (India time)</span>
            <select className="input-lux" value={r.hour} onChange={(e) => save({ hour: Number(e.target.value) })} data-testid="reminder-hour-select">
              {Array.from({ length: 24 }, (_, h) => <option key={h} value={h}>{hourLabel(h)}</option>)}
            </select></label>
        </div>
      )}
      <p className="mt-3 text-xs text-slate-400">Each family gets at most one reminder per period, and it stops automatically once they no longer match. Needs SMTP and automatic emails turned on.{info.last_run ? ` Last check ${new Date(info.last_run.at).toLocaleString()} · ${info.last_run.sent} sent.` : ""}</p>
    </div>
  );
};

const Templates = ({ data, onSaved }) => {
  const api = useApi();
  const keys = Object.keys(data.templates);
  const [key, setKey] = useState(keys[0]);
  const [t, setT] = useState(data.templates[keys[0]]);
  const [preview, setPreview] = useState(null);
  const [focus, setFocus] = useState("body");
  const refs = { subject: useRef(null), heading: useRef(null), body: useRef(null), button: useRef(null) };

  useEffect(() => { setT(data.templates[key]); }, [key, data.templates]);
  useEffect(() => {
    const h = setTimeout(() => { api("post", "/email/preview", t).then(setPreview).catch(() => {}); }, 400);
    return () => clearTimeout(h);
  }, [t, api]);

  const insert = (ph) => {
    const el = refs[focus].current;
    const v = t[focus] || "";
    const pos = el ? el.selectionStart ?? v.length : v.length;
    setT({ ...t, [focus]: `${v.slice(0, pos)}{{${ph}}}${v.slice(pos)}` });
  };
  const save = async () => { try { const r = await api("put", `/email/templates/${key}`, t); onSaved(key, r); toast.success("Template saved"); } catch (e) { toast.error(errMsg(e)); } };
  const reset = async () => { try { const r = await api("post", `/email/templates/${key}/reset`); setT(r); onSaved(key, r); toast.success("Reset to default"); } catch (e) { toast.error(errMsg(e)); } };
  const fld = (k, l, area) => (
    <label className="block"><span className="label-lux">{l}</span>
      {area ? <textarea ref={refs[k]} onFocus={() => setFocus(k)} className="input-lux min-h-[160px]" value={t[k] || ""} onChange={(e) => setT({ ...t, [k]: e.target.value })} data-testid={`template-${k}-input`} />
        : <input ref={refs[k]} onFocus={() => setFocus(k)} className="input-lux" value={t[k] || ""} onChange={(e) => setT({ ...t, [k]: e.target.value })} data-testid={`template-${k}-input`} />}
    </label>
  );
  return (
    <div>
      <div className="mb-4 flex flex-wrap gap-2">
        {keys.map((k) => <button key={k} onClick={() => setKey(k)} className={`rounded-full border px-4 py-1.5 text-sm ${key === k ? "border-[var(--ink)] bg-[var(--ink)] text-[var(--ivory)]" : "border-[var(--line)] bg-white"}`} data-testid={`template-tab-${k}`}>{data.templates[k].name}</button>)}
      </div>
      <div className="grid gap-6 lg:grid-cols-2">
        <div className="space-y-4 rounded-2xl border border-[var(--line)] bg-white p-6">
          {fld("subject", "Subject")}{fld("heading", "Heading")}{fld("body", "Body", true)}{fld("button", "Button text")}
          <div>
            <div className="label-lux">Insert placeholder into “{focus}”</div>
            <div className="flex flex-wrap gap-1.5">{data.placeholders.map((p) => <button key={p} type="button" onMouseDown={(e) => e.preventDefault()} onClick={() => insert(p)} className="rounded-full bg-[var(--ivory-2)] px-3 py-1 text-xs text-[var(--gold-deep)] hover:bg-[var(--line)]" data-testid={`placeholder-${p}`}>{`{{${p}}}`}</button>)}</div>
          </div>
          <div className="flex gap-2">
            <button className="btn-ink" onClick={save} data-testid="template-save-btn"><Save size={15} /> Save</button>
            <button className="btn-ghost" onClick={reset} data-testid="template-reset-btn"><RotateCcw size={15} /> Reset to default</button>
          </div>
          <ReminderCard tkey={key} />
        </div>
        <div className="overflow-hidden rounded-2xl border border-[var(--line)] bg-white" data-testid="template-preview">
          <div className="border-b border-[var(--line)] px-5 py-3 text-sm"><span className="text-slate-500">Subject: </span>{preview?.subject}</div>
          <iframe title="preview" srcDoc={preview?.html || ""} className="h-[560px] w-full" />
        </div>
      </div>
    </div>
  );
};

const Notifications = ({ data, onSaved }) => {
  const api = useApi();
  const [on, setOn] = useState(data.notifications.enabled);
  const [emails, setEmails] = useState(data.notifications.alert_emails.join("\n"));
  const save = async () => {
    try { onSaved(await api("put", "/notifications", { enabled: on, alert_emails: emails.split(/[\n,]/) })); toast.success("Notifications saved"); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="max-w-xl space-y-5 rounded-2xl border border-[var(--line)] bg-white p-6">
      <label className="flex items-center justify-between gap-3"><span><div>Automatic emails</div><div className="text-sm text-slate-500">Registration confirmations to guests and alerts to organizers. Invitations you send by hand always go out.</div></span>
        <Switch checked={on} onCheckedChange={setOn} data-testid="notifications-enabled-switch" /></label>
      <label className="block"><span className="label-lux">Organizer alert emails (one per line)</span>
        <textarea className="input-lux min-h-[110px]" value={emails} onChange={(e) => setEmails(e.target.value)} data-testid="notifications-emails-input" /></label>
      <button className="btn-ink" onClick={save} data-testid="notifications-save-btn"><Save size={15} /> Save</button>
    </div>
  );
};

const Log = () => {
  const api = useApi();
  const [rows, setRows] = useState(null);
  useEffect(() => { api("get", "/email/log").then(setRows); }, [api]);
  if (!rows) return <Loader2 className="mx-auto animate-spin text-[var(--gold)]" />;
  return (
    <div className="overflow-hidden rounded-2xl border border-[var(--line)] bg-white" data-testid="email-log">
      {rows.length === 0 && <p className="p-8 text-center text-slate-500">No emails yet.</p>}
      {rows.map((r) => (
        <div key={r.id} className="grid gap-1 border-b border-[var(--line)] px-5 py-3 text-sm last:border-0 sm:grid-cols-[150px_1fr_90px]">
          <span className="text-slate-500">{new Date(r.at).toLocaleString()}</span>
          <span><b className="font-medium">{r.to}</b> · {r.subject}{r.error && <span className="block text-xs text-red-700">{r.error}</span>}</span>
          <span className={r.status === "sent" ? "text-emerald-700" : r.status === "failed" ? "text-red-700" : "text-slate-500"}>{r.status}</span>
        </div>
      ))}
    </div>
  );
};

export default function EmailSettings() {
  const api = useApi();
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("smtp");
  useEffect(() => { api("get", "/email").then(setData).catch((e) => toast.error(errMsg(e))); }, [api]);
  if (!data) return <Loader2 className="mx-auto animate-spin text-[var(--gold)]" />;
  return (
    <div data-testid="email-page">
      <PageHead title="Email" text="Connect your own mailbox, edit the four guest emails and choose who gets alerts." />
      <div className="mb-6 flex w-fit gap-1 rounded-full border border-[var(--line)] bg-white p-1">
        {TABS.map(([k, l]) => <button key={k} onClick={() => setTab(k)} className={`rounded-full px-4 py-1.5 text-sm ${tab === k ? "bg-[var(--ink)] text-[var(--ivory)]" : "text-slate-600"}`} data-testid={`email-tab-${k}`}>{l}</button>)}
      </div>
      {tab === "smtp" && <Smtp data={data} onSaved={setData} />}
      {tab === "templates" && <Templates data={data} onSaved={(k, t) => setData((d) => ({ ...d, templates: { ...d.templates, [k]: { ...d.templates[k], ...t } } }))} />}
      {tab === "notifications" && <Notifications data={data} onSaved={(n) => setData((d) => ({ ...d, notifications: n }))} />}
      {tab === "log" && <Log />}
      {tab === "bulk" && <BulkEmail templates={data.templates} />}
    </div>
  );
}
